import React, { useCallback, useState } from 'react';
import '../../Header/JobLoadsManagementHeader/JobLoadsManagementHeader.scss';

import './DriverPool.scss';
import { Link, useParams } from 'react-router-dom';
import { Routes } from '../../../utils';
import { PermissionEnum } from '../../../types/PermissionEnum';
import { useAuthContext } from '../../Auth/AuthProvider';
import AddDriversToPool from './AddDriversToPool/AddDriversToPool';
import {
    GET_AVAILABLE_DRIVERS_FOR_JOB,
    GET_JOB_PROFILE,
    GET_SELECTED_DRIVERS_FOR_JOB,
} from '../../../graphql/client/queries';
import { useMutation, useQuery } from '@apollo/client/react';
import { CombinedGraphQLErrors } from '@apollo/client';
import { Query } from '../../_common';
import {
    GetFullJobProfile,
    GetSelectedDriversForJob,
    GetSelectedDriversForJob_getSelectedDriversForJob as SelectedDrivers,
} from '../../../types/schemaTypes';
import { ADD_AND_SAVE_DRIVERS_TO_POOL } from '../../../graphql/client/mutations';
import { Error, Loading } from '../../_common';
import SelectedDriversForPoolList from './SelectedDriversForPoolList';

import { ToastContainer, toast } from 'react-toastify';
import { toastOpt } from '../../../utils/helpers/toastHelper'
import { Alert, Dropdown } from 'react-bootstrap';
import { AnyNaptrRecord } from 'dns';
import { JobStatusEnum } from '../../../types/JobStatusEnum';
import JobMenuItems from '../../Header/JobMenuItemsHeader/JobMenuItems';

const PAGE_SIZE = 50;

const toasterOptions = { duration: 2500 };

const ErrorMessages = {
    update: 'Failed to add drivers to pool',
    notesLengthMax: 'Failed: Notes cannot have more than 50 characters',
};

const Messages = {
    Save: 'Notes saved',
};

const DriverPool = ({ match }: any): React.ReactElement => {
    const authContext = useAuthContext();
    const [showModal, setShowModal] = useState(false);
    const [refetched, setRefetched] = useState(false);
    const [listHeight, setListHeight] = useState(window.screen.height);
    const [isEndOfResults, setIsEndOfResults] = useState(false);
    const [searchPhrase, setSearchPhrase] = useState('');
    const isDisplayLoadsEnabled = authContext.userHasPermission(PermissionEnum.ViewLoads);
    const canEditDriversInDriverPool = useAuthContext().userHasPermission(PermissionEnum.EditJob);

    const canEditDriverPool = useAuthContext().userHasPermission(PermissionEnum.EditDriverPool)

    const { jobId } = useParams()
    const jobIdVal: any = jobId

    const variables = {
        jobId: parseInt(jobIdVal),
    };

    const handleClickBack = (): void => {
        setShowModal(false);
    };

    const { data: jobProfileData } = useQuery<GetFullJobProfile>(GET_JOB_PROFILE, {
        variables,
        fetchPolicy: 'cache-and-network',
        pollInterval: 300000,
    });

    const renderToast = (message, type = 'info') => {
        interface ToastProps {
            onClose?: () => void;
        }
        const Comp = (props: ToastProps) => {
            return (
                <Alert variant={type} onClose={props.onClose}>
                    {message}
                </Alert>
            );
        };

        Comp.displayName = 'toast';
        return Comp;
    };

    const onCompletedsaveSelectedDriversToPool = () => {
        setRefetched(true);
    };

    const onActionError = action => {
        toast.error(ErrorMessages[action], toastOpt);
    };

    const [saveSelectedDriversToPool, { loading: mutationLoading, error: mutationError }] = useMutation(
        ADD_AND_SAVE_DRIVERS_TO_POOL,
        {
            onCompleted: onCompletedsaveSelectedDriversToPool,
            onError: () => onActionError('update'),
            refetchQueries: () => {
                return [
                    {
                        query: GET_SELECTED_DRIVERS_FOR_JOB,
                        variables: { jobId: parseInt(jobIdVal) },
                    },
                    {
                        query: GET_AVAILABLE_DRIVERS_FOR_JOB,
                        variables: { jobId: parseInt(jobIdVal) },
                    },
                ]
            },
        },
    );

    const onDelete = useCallback(async (record: any) => {
        const variables = {
            input: {
                jobId: parseInt(jobIdVal),
                notes: '',
                action: 'removeDriver',
                driverIds: record,
            },
        };
        await saveSelectedDriversToPool({ variables });
    }, []);

    const handleOnSave = async (driverIds?: (number | null | undefined)[]): Promise<void> => {
        setShowModal(false);
        const variables = {
            input: {
                jobId: parseInt(jobIdVal),
                notes: '',
                action: 'saveDriverPool',
                driverIds,
            },
        };
        await saveSelectedDriversToPool({ variables });
    };

    const onCompleted = action => {
        toast.success(Messages[action], toastOpt);
    };

    const handleSaveDriverNotes = useCallback(async (record: any) => {
        const variables = {
            input: {
                jobId: parseInt(jobIdVal),
                notes: record.notes,
                action: 'updatenote',
                driverIds: record.driverId,
            },
        };
        if (record.notes.length > 50) {
            onActionError('notesLengthMax');
        } else {
            await saveSelectedDriversToPool({ variables });
            onCompleted('Save');
        }
    }, []);

    return (
        <Query<GetSelectedDriversForJob> query={GET_SELECTED_DRIVERS_FOR_JOB} variables={variables} fetchPolicy="no-cache">
            {({
                error,
                data,
                loading,
                refetch,
                fetchMore,
            }): React.ReactElement | null => {

                const offset = data && data.getSelectedDriversForJob ? data.getSelectedDriversForJob.length / PAGE_SIZE : 0;

                React.useEffect(() => {
                    if (
                        offset % 1 !== 0 ||
                        (!isEndOfResults &&
                            data &&
                            data.getSelectedDriversForJob &&
                            data.getSelectedDriversForJob.length < PAGE_SIZE)
                    ) {
                        // If offset is not a whole number, the end of the results has been reached. (8/22/19) - BS
                        setIsEndOfResults(true);
                    }
                });

                if (error) {
                    return <Error error={error} />;
                }

                if (mutationError) {
                    return <Error error={mutationError} />;
                }

                let selectedDrivers: SelectedDrivers[] = [];

                document.title =
                    jobProfileData?.getJobProfile?.jobDetails?.jobName ?? localStorage.getItem('Jobdetails-TabName') ?? '';

                if (data?.getSelectedDriversForJob && data.getSelectedDriversForJob.length !== 0) {
                    (data.getSelectedDriversForJob as (SelectedDrivers | null)[]).forEach((element: SelectedDrivers | null): void => {
                        if (element != null) {
                            selectedDrivers.push(element);
                        }
                    });
                }

                // Drivers Search/ Filtering
                if (searchPhrase && searchPhrase.length > 0) {
                    selectedDrivers = selectedDrivers.filter(driver => {
                        return (
                            driver &&
                            (driver.name?.toLowerCase().includes(searchPhrase.toLowerCase()) ||
                                driver.companyName?.toLowerCase().includes(searchPhrase.toLowerCase()))
                        );
                    });
                }

                return (
                    <div className="Messages-Container">
                        <ToastContainer />
                        <div id="loadsListContainer" className="JobActivity-List" style={{ height: listHeight }}>
                            <div className="JobProfile-Header">
                                <div className="SelectedDrivers-SearchBar">
                                    <i className="material-icons SearchBar-Icon">search</i>
                                    <input
                                        type="text"
                                        id="drivers-search"
                                        className="SearchBar-Input"
                                        placeholder="Search"
                                        onInput={() => {
                                            setSearchPhrase(
                                                (document.getElementById('drivers-search') as HTMLInputElement).value,
                                            );
                                        }}
                                    />
                                </div>

                                {(canEditDriverPool) && jobProfileData?.getJobProfile?.jobDetails?.status?.status !==
                                    JobStatusEnum.COMPLETED.toString() ? (
                                    <div className="Add-Driver-Container Row" onClick={() => setShowModal(true)}>
                                        <i className="material-icons Add-Driver-Icon">add_circle</i>
                                        <span className="Add-Driver-Link">Add Driver to Pool</span>
                                    </div>
                                ) : (
                                    ''
                                )}

                                <JobMenuItems jobId={jobId ?? "0"} menu='driverPool'/>
                            </div>

                            <div className="DriverPool-Bottom-Container row">
                                <div className="DriverPool-Header-Title col-md-3">Driver Name</div>
                                <div className="DriverPool-Header-Title col-md-3">Carrier Name</div>
                                <div className="DriverPool-Header-Title col-md-3">Notes</div>
                                <div className="DriverPool-Header-Title col-md-3">Actions</div>
                            </div>
                            {mutationLoading ? (
                                <div className="DriverPool-Spinner">
                                    <Loading />
                                </div>
                            ) : (
                                ''
                            )}
                            {loading ? (
                                <div className="DriverPool-Spinner">
                                    <Loading />
                                </div>
                            ) : selectedDrivers.length > 0 ? (
                                <SelectedDriversForPoolList
                                    selectedDrivers={selectedDrivers}
                                    refetched={refetched}
                                    setRefetched={setRefetched}
                                    refetch={refetch}
                                    isLoading={loading}
                                    canEditDriversInDriverPool={canEditDriversInDriverPool}
                                    isEndOfResults={isEndOfResults}
                                    onSaveNotes={handleSaveDriverNotes}
                                    onDelete={onDelete}
                                    onLoadMore={() =>
                                        fetchMore({
                                            variables: {
                                                offset,
                                            },
                                            updateQuery: (prev, { fetchMoreResult }) => {
                                                if (
                                                    prev &&
                                                    fetchMoreResult &&
                                                    prev.getSelectedDriversForJob &&
                                                    fetchMoreResult.getSelectedDriversForJob
                                                ) {
                                                    return Object.assign({}, prev, {
                                                        getSelectedDriversFiltered: [
                                                            ...prev.getSelectedDriversForJob,
                                                            ...fetchMoreResult.getSelectedDriversForJob,
                                                        ],
                                                    });
                                                } else {
                                                    return prev;
                                                }
                                            },
                                        })
                                    }
                                />
                            ) : searchPhrase ? (
                                <div className="NoResult-Container Driverpool-NoResult-Container">
                                    <div className="DriverPool-NoResult-Title">No matching items found.</div>
                                </div>
                            ) : (
                                <div className="NoResult-Container Driverpool-NoResult-Container">
                                    <div className="DriverPool-NoResult-Title">No Drivers set up in the pool.</div>
                                </div>
                            )}
                        </div>
                        <div>
                            <AddDriversToPool
                                show={showModal}
                                onClickBack={handleClickBack}
                                onClickSave={handleOnSave}
                                wellhead={jobProfileData?.getJobProfile?.jobDetails?.jobName ?? undefined}
                                jobId={parseInt(jobIdVal)}
                                isDisabled={mutationLoading}
                                refetched={refetched}
                                setRefetched={setRefetched}
                            />
                        </div>
                    </div>
                );
            }}
        </Query>
    );
};

export default DriverPool;
